/* ══════════════════════════════════════════════════════
   BADGES — single canonical badge catalogue.

   Used by:
   - src/hooks/useBadgeChecker.js (awards via earnBadge)
   - src/app/pages/Journey/tabs/BadgesTab.jsx (UI render)
   - src/stores/gamificationStore.js (initial availableBadges)

   Each badge has a stable id, presentation fields, a
   category, and a pure `check(d)` predicate. The shape
   of `d` is documented below — every consumer must
   compute the same shape.

   Spec: 2026-04-29 week-4 reconciliation plan §4.3.
   ══════════════════════════════════════════════════════ */

/**
 * @typedef {Object} BadgeData
 * @property {number} streak           — days, current streak from gamificationStore
 * @property {number} savRate          — % savings rate this month (income > 0 required)
 * @property {boolean} hasTrackedSpending — at least one positive expense this month
 * @property {number} goalCount        — count of active goals (current < target)
 * @property {number} goalsCompleted   — count of completed goals (current ≥ target)
 * @property {number} netWorth         — total assets − total debts (cents-as-dollars)
 * @property {number} totalCardDebt    — sum of all card balances
 * @property {boolean} hasCards        — at least one tracked card
 * @property {number} cardsPayedOff    — count of cards with balance === 0 and limit > 0
 * @property {number} stage            — current stage from useCurrentStage
 */

export const BADGES = [
  // habit
  { id: 'streak3',    icon: '🔥',  name: 'First Flame',     desc: '3-day streak',      category: 'habit',   check: d => d.streak >= 3 },
  { id: 'streak7',    icon: '⚡',  name: 'Week Warrior',    desc: '7-day streak',      category: 'habit',   check: d => d.streak >= 7 },
  { id: 'streak30',   icon: '💎',  name: 'Diamond Habit',   desc: '30-day streak',     category: 'habit',   check: d => d.streak >= 30 },
  { id: 'streak90',   icon: '👑',  name: 'Royalty',         desc: '90-day streak',     category: 'habit',   check: d => d.streak >= 90 },
  { id: 'streak180',  icon: '🏆',  name: 'Legend',          desc: '180-day streak',    category: 'habit',   check: d => d.streak >= 180 },

  // savings
  // WHY: zero logged spending made a brand-new plan look like a 100% savings
  // month. Require observed spending before recognizing a savings rate.
  { id: 'sav10',      icon: '💰',  name: 'Saver',           desc: '10% savings rate',  category: 'savings', check: d => d.hasTrackedSpending && d.savRate >= 10 },
  { id: 'sav20',      icon: '💰',  name: 'Power Saver',     desc: '20% savings rate',  category: 'savings', check: d => d.hasTrackedSpending && d.savRate >= 20 },
  { id: 'sav30',      icon: '🏅',  name: 'Elite Saver',     desc: '30% savings rate',  category: 'savings', check: d => d.hasTrackedSpending && d.savRate >= 30 },

  // goals
  { id: 'goal1',      icon: '🎯',  name: 'Goal Setter',     desc: 'First goal created', category: 'goals',  check: d => d.goalCount >= 1 },
  { id: 'goal3',      icon: '🎯',  name: 'Dreamer',         desc: '3 active goals',     category: 'goals',  check: d => d.goalCount >= 3 },
  { id: 'goalreach',  icon: '🌟',  name: 'Goal Crusher',    desc: 'First goal completed', category: 'goals', check: d => d.goalsCompleted >= 1 },

  // wealth
  { id: 'nwpos',      icon: '📈',  name: 'Net Positive',    desc: 'Net worth above zero', category: 'wealth', check: d => d.netWorth > 0 },
  { id: 'nw10k',      icon: '💵',  name: 'Five Figures',    desc: 'Net worth $10,000+', category: 'wealth',  check: d => d.netWorth >= 10000 },
  { id: 'nw50k',      icon: '🏦',  name: 'Wealth Builder',  desc: 'Net worth $50,000+', category: 'wealth',  check: d => d.netWorth >= 50000 },

  // debt
  { id: 'debtpay',    icon: '✂️',  name: 'Debt Cutter',     desc: 'Paid off a credit card', category: 'debt', check: d => d.cardsPayedOff >= 1 },
  { id: 'debtfree',   icon: '🕊️',  name: 'Debt Free',       desc: 'Zero card balance',  category: 'debt',    check: d => d.totalCardDebt === 0 && d.hasCards },

  // journey (mapped to the 5-stage Karat Path)
  { id: 'stage2',     icon: '🔍',  name: 'Clarity',         desc: 'Reached Stage 2',    category: 'journey', check: d => d.stage >= 2 },
  { id: 'stage3',     icon: '🛡️',  name: 'Armor',           desc: 'Reached Stage 3',    category: 'journey', check: d => d.stage >= 3 },
  { id: 'stage4',     icon: '🚀',  name: 'Acceleration',    desc: 'Reached Stage 4',    category: 'journey', check: d => d.stage >= 4 },
  { id: 'stage5',     icon: '📈',  name: 'Growth',          desc: 'Reached Stage 5',    category: 'journey', check: d => d.stage >= 5 },
];

export const BADGE_CATEGORIES = ['all', 'habit', 'savings', 'goals', 'wealth', 'debt', 'journey'];

// Old (pre-week-4) ids that map cleanly to the new canonical ids. Used by
// the gamificationStore v2→v3 migration to preserve already-earned badges.
// Old ids without an entry here are dropped — they had no equivalent under
// the canonical catalogue and weren't user-visible after week-4 anyway.
export const LEGACY_BADGE_ID_MAP = {
  'week-streak':  'streak7',
  'month-streak': 'streak30',
  'goal-set':     'goal1',
  'goal-reached': 'goalreach',
  'net-worth-up': 'nwpos',
  'debt-paid':    'debtpay',
};
